/**
 * #1590 — zet elke gezinslijn (family-unit, één per huwelijk) op de definitieve
 * kaartposities. De stam vertrekt vanuit het eigen paar: midden in de opening
 * direct naast de betreffende partner, aan de kant van de bloedlijn-ouder (bij
 * het eerste huwelijk is dat de opening tussen de twee echtgenoten). Zonder
 * zichtbare partner vertrekt hij onder de ouder. De dropdowns eindigen op de
 * kaart van elk kind.
 *
 * Aangeroepen na élke stap die kaarten verschuift (attachSpouses, overlap-pass,
 * scherm-partner-spacing), want die zetten de linkcoördinaten terug op de ouder.
 */
import type { LayoutLink, LayoutNode } from '../types.ts';

/** #1808 — id van de verwijskaart van `partner` naast `anker` (huwelijk tussen verwanten). */
export const verwijsKaartId = (anker: string, partner: string) => `__verwijs__${anker}__${partner}`;

export function plaatsGezinslijnen(nodes: LayoutNode[], links: LayoutLink[]): void {
  const opId = new Map(nodes.map((n) => [n.person.id, n]));
  const breedte = (n: LayoutNode) => n.nodeWidth || 230;
  for (const link of links) {
    if (link.type !== 'family-unit' || !link.childIds) continue;
    const ouder = opId.get(link.sourceId);
    if (!ouder) continue;
    const partnerId = link.parentIds?.[1];
    // #1808 — staat de partner elders in de boom (huwelijk tussen verwanten), dan is de
    // verwijskaart naast de ouder het paar; de stam vertrekt daar.
    const partner = partnerId
      ? (opId.get(verwijsKaartId(ouder.person.id, partnerId)) ?? opId.get(partnerId))
      : undefined;
    let x = ouder.x;
    if (partner && Math.abs(partner.y - ouder.y) < 1) {
      // De buur van de partner aan de kant van de ouder (de ouder zelf, of een
      // eerdere partner die ertussen staat).
      const links_ = partner.x > ouder.x;
      let buur = ouder;
      // Staat er een andere bloedlijnkaart tussen (neven die met elkaar trouwen),
      // dan hoort de stam niet bij die verre partner maar onder de ouder.
      let vreemdTussen = false;
      for (const n of nodes) {
        if (Math.abs(n.y - ouder.y) >= 1 || n === partner || n === ouder) continue;
        const tussen = links_ ? n.x > ouder.x && n.x < partner.x : n.x < ouder.x && n.x > partner.x;
        if (!tussen) continue;
        if (!n.isSpouseNode) vreemdTussen = true;
        if (links_ ? n.x > buur.x : n.x < buur.x) buur = n;
      }
      if (vreemdTussen) {
        link.sourceX = ouder.x;
        link.sourceY = ouder.y;
        link.childXs = link.childIds.map(
          (id, i) => opId.get(id)?.x ?? link.childXs?.[i] ?? ouder.x
        );
        link.targetX = link.childXs[0];
        continue;
      }
      x = links_
        ? (buur.x + breedte(buur) / 2 + partner.x - breedte(partner) / 2) / 2
        : (buur.x - breedte(buur) / 2 + partner.x + breedte(partner) / 2) / 2;
    }
    link.sourceX = x;
    link.sourceY = ouder.y;
    link.childXs = link.childIds.map((id, i) => opId.get(id)?.x ?? link.childXs?.[i] ?? x);
    link.targetX = link.childXs[0];
  }
}
